import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Subscribe to a single active cart instance for checkout.
 *
 * NOTE: Only carts with status === 'active' are accepted. Failed/retry carts may
 * be blocked here even when checkoutOrderGuard supports replacing a pending
 * orderid — intentional parity with the previous CheckoutPage behavior.
 */
export function useCheckoutCart({ currentUser, supermarketId, cartInstanceId }) {
  const [cartInstance, setCartInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setCartInstance(null);
      setError('Please log in to proceed to checkout.');
      setLoading(false);
      return undefined;
    }

    if (!supermarketId || !cartInstanceId) {
      setCartInstance(null);
      setError('Supermarket or Cart Instance ID is missing.');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const cartRef = doc(db, 'Carts', currentUser.uid);
    const unsubscribe = onSnapshot(
      cartRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const cartData = docSnapshot.data();
          const instance = cartData[supermarketId]?.[cartInstanceId];
          if (instance?.products?.length > 0 && instance.status === 'active') {
            setCartInstance(instance);
            setError(null);
          } else {
            setError(
              `This cart instance (ID: ${cartInstanceId.replace('cart_instance_', '')}) is empty, not found, or not active.`
            );
            setCartInstance(null);
          }
        } else {
          setError('No cart found for this user.');
          setCartInstance(null);
        }
        setLoading(false);
      },
      () => {
        setError('Failed to load cart details for checkout.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser, supermarketId, cartInstanceId]);

  return { cartInstance, loading, error };
}
